<template>
  <component
    :is="as"
    ref="pictogramsRef"
    class="AiPictograms"
    :class="{
      [`AiPictograms--${variant}`]: Boolean(variant),
      [`AiPictograms--${color}`]: Boolean(color),
      [`AiPictograms--facilities`]: Boolean(facilities),
      'AiPictograms--noTitle': !title && inline,
    }">
    <ai-typo v-if="label" as="span" class="sr-only">{{ label }}</ai-typo>
    <ai-external-icon
      v-if="url"
      :size="size"
      :url="url"
      :icon-position="iconPosition"
      :class="{ [`AiPictograms--spaced`]: spaced }" />
    <ai-icon
      v-else-if="iconName"
      :size="size"
      :name="iconName"
      :namespace="iconNamespace"
      :color="color"
      :class="{ [`AiPictograms--spaced`]: spaced }" />
    <ai-typo
      v-if="title"
      as="span"
      class="AiPictograms-label"
      variant="icon-text"
      :color="typoColor">
      {{ title }}
    </ai-typo>

    <div v-if="showToolTip" class="AiPictograms-tooltipBody">
      <ai-external-icon
        v-if="url"
        :size="size"
        :url="url"
        :icon-position="iconPosition" />
      <ai-typo
        v-if="label"
        as="span"
        variant="legal-regular"
        color="stratos-900">
        {{ label }}
      </ai-typo>
    </div>
  </component>
</template>

<script setup lang="ts">
import AiExternalIcon from '../AiIcon/AiExternalIcon.vue';
import type { GenericIconName, IconNameSpace } from '../AiIcon/types';
import AiTypo from '../AiTypo/AiTypo.vue';
import type { AiTypoVariant } from '../AiTypo/types';

export interface AiPictoProps {
  as?: keyof HTMLElementTagNameMap;
  size?: number;
  title?: string | null;
  label?: string | null;
  url?: string;
  iconName?: GenericIconName;
  iconNamespace?: IconNameSpace;
  inline?: boolean;
  variant: 'positive' | 'inverse';
  iconPosition?: 'top' | 'center' | 'end';
  spaced?: boolean;
  facilities?: boolean;
  color?: 'gold-300' | 'white';
  typoColor?: 'gold-900' | 'white';
  typoVariant?: AiTypoVariant;
  showToolTip?: boolean;
}

withDefaults(defineProps<AiPictoProps>(), {
  as: 'span',
  color: undefined,
  facilities: false,
  label: '',
  iconPosition: 'center',
  iconName: undefined,
  iconNamespace: undefined,
  inline: false,
  size: 24,
  title: '',
  typoColor: 'gold-900',
  typoVariant: 'icon-text',
  url: undefined,
  showToolTip: false,
});
</script>
<style scoped lang="scss">
@use '@/assets/styles/utilities/mixins';
@use '@/assets/styles/utilities/colors';
@use '@/assets/styles/utilities/constants';
@use '@/assets/styles/utilities/mq';

.AiPictograms {
  display: flex;
  flex-direction: column;
  align-items: center;
  color: colors.$stratos-900;
  position: relative;

  &--spaced {
    margin-right: constants.$margin-01;
  }

  &--inverse {
    fill: colors.$white;
    color: colors.$white;
    p {
      color: colors.$white;
    }
  }

  &-label {
    width: 100%;
    margin-top: constants.$inner-01;
    text-align: center;
    white-space: normal;
    overflow: hidden;
    text-overflow: ellipsis;

    @media (mq.$from-medium) {
      margin-top: constants.$inner-03;
    }
  }

  &--noTitle {
    display: inline;
  }

  &--gold-300 {
    fill: colors.$gold-300;
    color: colors.$gold-300;
  }

  &--white {
    fill: colors.$white;
    color: colors.$white;
  }

  &--facilities {
    flex-direction: row;

    .AiPictograms-label {
      margin-top: 0;
      text-align: start;
    }

    p {
      @include mixins.rem-fallback(font-size, 12);
    }
  }

  /* Tooltip */
  .AiPictograms-tooltipBody {
    width: max-content;
    height: auto;
    background-color: colors.$neutral-100;
    border-radius: 6px;
    position: absolute;
    z-index: -1;
    top: -210%;
    display: flex;
    justify-content: center;
    align-items: center;
    gap: constants.$inner-01;
    padding: 1rem 0.5rem;
    opacity: 0;
    transition: opacity 300ms ease-in-out;

    @media (mq.$from-medium) {
      left: 50%;
      transform: translateX(-30%);
    }
  }

  &:hover .AiPictograms-tooltipBody {
    opacity: 1;
    z-index: 1;
  }

  .AiPictograms-tooltipBody::after {
    content: '';
    position: absolute;
    top: 100%;
    border-width: 5px;
    border-style: solid;
    border-color: colors.$neutral-100 transparent transparent transparent;

    @media (mq.$from-medium) {
      left: 30%;
      transform: translateX(-50%);
    }
  }

  .tooptip-enter-active,
  .tooptip-leave-active {
    transition: opacity 0.5s ease;
  }
  .tooptip-enter-from,
  .tooptip-leave-to {
    opacity: 0;
  }
}
</style>
